/*
 * Who has filled their board in, and who still owes.
 *
 * This is the half of the week before anything kicks off, when the screen is a
 * nagging list rather than a scoreboard. Points cannot order it - everybody has
 * nought - and a leader ring cannot mark anyone, because everybody is tied for
 * first and gold that everyone has is not a signal.
 *
 * So the ordering and the colour come from readiness instead: the people being
 * waited on at the top, the people who are done at the bottom where they need
 * no attention.
 */

export type EntryState = 'empty' | 'partial' | 'done'

export interface Entry {
  name: string
  /** Picks made on this week's board. */
  made: number
  /** Games on the board. Zero before the commissioner publishes. */
  total: number
  pts: number
}

/**
 * Whether a player has started, is midway, or is finished.
 *
 * A board of NOTHING is not a finished board. Before a board is published
 * there is nothing to pick, and calling the whole family "done" would turn the
 * one screen that chases people into a screen saying all is well.
 */
export function entryState(e: Pick<Entry, 'made' | 'total'>): EntryState {
  if (e.total <= 0 || e.made <= 0) return 'empty'
  return e.made >= e.total ? 'done' : 'partial'
}

const RANK: Record<EntryState, number> = { empty: 0, partial: 1, done: 2 }

/**
 * Unstarted first, then partial, then finished.
 *
 * Within the partial group, whoever owes the most comes first: one game left is
 * nearly done and nine is barely begun, and the point of this order is to put
 * the person worth chasing at the top.
 *
 * Ties break on name so the list does not jitter between refreshes - the screen
 * now refetches on its own, and a roster that reshuffles under a thumb every
 * thirty seconds is worse than one in an imperfect order.
 */
export function byReadiness(a: Entry, b: Entry): number {
  const ra = RANK[entryState(a)]
  const rb = RANK[entryState(b)]
  if (ra !== rb) return ra - rb
  const owed = (e: Entry) => Math.max(0, e.total - e.made)
  if (owed(a) !== owed(b)) return owed(b) - owed(a)
  return a.name.localeCompare(b.name)
}
